import { NgClass } from '@angular/common';
import { Component, DestroyRef, Input, OnChanges, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetBuilderConfig, MsPage, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { DecimalsPriceFormatterService } from '@frontend/sports/odds/feature/native-price-decimal-formatter';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BetBuilderHelpPageMessages } from '@frontend/sports/types/components/sitecore';
import { UserService } from '@frontend/sports/user/feature';
import { OutsideInteractionDirective } from '@frontend/sports/web/directives-ui';

import { BetBuilderTrackingValues } from '../../bet-builder-tracking';

@Component({
    selector: 'ms-bet-builder-help',
    templateUrl: './bet-builder-help.component.html',
    imports: [OutsideInteractionDirective, NgClass],
})
export class BetBuilderHelpComponent implements OnInit, OnChanges {
    private sitecore = inject(Sitecore);
    private msPage = inject(MsPage);
    private config = inject(BetBuilderConfig);
    private decimalsPriceFormatter = inject(DecimalsPriceFormatterService);
    private userService = inject(UserService);
    private tracker = inject(TrackingService);
    private activeModal = inject(ActiveModal, { optional: true });

    @Input() collapsible?: boolean;
    @Input() showHeader = true;
    @Input() sportId = SportConstant.Soccer;
    @Input() eventName: string;
    @Input() leagueId: number;
    @Input() showHelpButtons = false;

    translations: BetBuilderHelpPageMessages;
    helpPageLegs: string[];
    helpPageLink: string;
    helpPageLearnMoreLink: string;
    helpPageContent: string;
    collapsed: boolean;
    exampleEventName: string;
    exampleOdds: string;
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        if (this.sitecore.betBuilderHelpPage.messages) {
            this.translations = this.sitecore.betBuilderHelpPage.messages;
            this.helpPageLegs = this.translations.exampleLegs.split(';');
            this.getSportTranslations();
            this.userService.onUserLogin$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.rebindOdds());
            this.helpPageLink = this.translations.helpPageLink;
            this.helpPageLearnMoreLink = this.translations.helpPageLearnMoreLink;
            this.helpPageContent = this.translations.helpPageContent.replace(/{labelName}/g, `<span class="label-name">${this.msPage.title}</span>`);
            this.trackLoad();
        }
    }

    close(): void {
        this.activeModal?.close();
    }

    ngOnChanges(): void {
        this.collapsed = this.isPrecreatedBetBuilderEnabled && (this.collapsible ?? false);
    }

    toggleHelp(): void {
        if (!this.isPrecreatedBetBuilderEnabled || !this.collapsible) {
            return;
        }

        this.collapsed = !this.collapsed;
    }

    get isPrecreatedBetBuilderEnabled(): boolean {
        return this.config.isPrecreatedBetBuilderEnabled;
    }

    get okButtonText(): string {
        return this.sitecore.betBuilderEntainUI.messages?.OverlayOkButton ?? '';
    }

    onOkClicked(): void {
        this.trackButtonClick(BetBuilderTrackingValues.OkGotItCta);
        this.close();
    }

    onLearnMoreClick(): void {
        this.trackButtonClick(BetBuilderTrackingValues.LearnMoreCta);
    }

    private trackLoad(): void {
        this.tracker.track(trackingConstants.EVENT_CONTENT_VIEW, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.showHelpButtons ? BetBuilderTrackingValues.InfoDialog : NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.eventName,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.showHelpButtons ? this.eventName : BetBuilderTrackingValues.EventInfoPopup,
        });
    }

    private trackButtonClick(action: BetBuilderTrackingValues.OkGotItCta | BetBuilderTrackingValues.LearnMoreCta): void {
        this.tracker.track(trackingConstants.EVENT_TRACKING, {
            ...this.getTrackingObject(),
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetBuilderTrackingValues.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.showHelpButtons ? BetBuilderTrackingValues.InfoDialog : NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: BetBuilderTrackingValues.InfoDialog,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: action,
        });
    }

    private getTrackingObject = () => {
        return {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetBuilderTrackingValues.BetBuilder,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetBuilderTrackingValues.BuildaBetPod,
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.SPORT_ID]: this.sportId.toString(),
            [trackingConstants.SPORT_LEAGUE_ID]: this.leagueId.toString(),
        };
    };

    private getSportTranslations(): void {
        const sportName = SportConstant[this.sportId].toString();

        const eventNameWithLeagueId = this.translations[`exampleEventName${sportName}_${this.leagueId}` as keyof BetBuilderHelpPageMessages];
        const eventName = this.translations[`exampleEventName${sportName}` as keyof BetBuilderHelpPageMessages];

        const legsWithLeagueId = this.translations[`exampleLegs${sportName}_${this.leagueId}` as keyof BetBuilderHelpPageMessages];
        const legs = this.translations[`exampleLegs${sportName}` as keyof BetBuilderHelpPageMessages];

        this.rebindOdds();
        this.exampleEventName = eventNameWithLeagueId ?? eventName;
        this.helpPageLegs = (legsWithLeagueId ?? legs).split(';');
    }

    private rebindOdds(): void {
        const sportName = SportConstant[this.sportId].toString();
        const oddsWithLeagueId = this.translations[`exampleOdds${sportName}_${this.leagueId}` as keyof BetBuilderHelpPageMessages];
        const odds = this.translations[`exampleOdds${sportName}` as keyof BetBuilderHelpPageMessages];
        this.exampleOdds = this.decimalsPriceFormatter.format(Number(oddsWithLeagueId ?? odds));
    }
}
